import { z } from 'zod';
import type { DataPolicy, DataPolicyBody } from '../dataPoliciesApi';

const termSchema = z
  .object({
    label: z.string().min(1),
    op: z.enum(['in', 'exists']),
    values: z.array(z.string()),
  })
  .refine((term) => term.op === 'exists' || term.values.length > 0, {
    message: 'Select at least one value.',
    path: ['values'],
  });

export const dataPolicyFormSchema = z.object({
  name: z.string().trim().min(1, 'Policy name is required.').max(255),
  terms: z.array(termSchema).min(1, 'Add at least one condition.'),
});

export type DataPolicyFormValues = z.infer<typeof dataPolicyFormSchema>;
export type DataPolicyTermValues = DataPolicyFormValues['terms'][number];

export const toFormValues = (
  policy: DataPolicy | undefined,
  name = policy?.name ?? '',
): DataPolicyFormValues => ({
  name,
  terms: (policy?.condition.terms ?? []).map((term) => ({
    label: term.label,
    op: term.op,
    values: 'values' in term ? term.values : [],
  })),
});

export const toCondition = (
  terms: readonly DataPolicyTermValues[],
): DataPolicyBody['condition'] => ({
  version: 1,
  match: 'all',
  terms: terms.map((term) =>
    term.op === 'exists'
      ? { label: term.label, op: 'exists' as const }
      : { label: term.label, op: 'in' as const, values: term.values },
  ),
});

/** A condition the backend can evaluate: every `in` term has values. */
export const isCompleteCondition = (
  terms: readonly DataPolicyTermValues[],
): boolean =>
  terms.length > 0 &&
  terms.every((term) => term.op === 'exists' || term.values.length > 0);
